import { useState, useEffect, useMemo } from 'react';

import {
    FastChargeModel,
    PlugAndChargeModel,
    SafetyChargingModel,
    ScheduleChargingModel,
    DynamicPriceChargingModel,
    SolarAssistedChargingModel,
    GenericSmartChargingModelModeEnum,
    ProgramChargingModel,
    GridxSmartChargingResponseModel,
} from '@swagger-http';

import { TelemetryTypes } from '@store/enums';
import { checkForScopes } from '@tools/utils';
import { ChargingModeData } from '@store/types';
import { MarketingTileModel } from '@tools/types';
import { isTheWallboxCharging } from './shared';
import { MarketingTileTypes, Scope } from '@tools/enums';
import {
    useAppSelector,
    useTelemetries,
    useIsGridXCustomer,
    useHasEmobilityTokenExpired,
    useEmobilityReadingStartDate,
} from '@store/selectors';

const {
    Schedule,
    DynamicPriceCharging,
    GridFriendlyCharging,
    SolarAssistedCharging,
    HomePlugAndCharge,
    ProgramCharging,
    SafetyCharging,
    FastCharging,
    Autopilot,
    GridxSmartCharging,
} = GenericSmartChargingModelModeEnum;

export const useHasWallbox = () =>
    !!useAppSelector((state) => state.emobility.wallboxData.wallbox?.configId);

export const useWallboxConfigId = () =>
    useAppSelector((state) => state.emobility.wallboxData.wallbox?.configId);

export const useWallboxFirmwareUpdateSettings = () =>
    useAppSelector(
        (state) => state.emobility.wallboxData.firmware.updateSettings,
    );

export const useHasWallboxTokenExpired = () => {
    const tokenExpiryDate = useAppSelector(
        (state) =>
            state.emobility.wallboxData.wallbox?.deviceGateway?.tokenExpiryDate,
    );

    return useHasEmobilityTokenExpired(tokenExpiryDate);
};

export const useIsWallboxCharging = () => {
    const [isCharging, setIsCharging] = useState(false);
    const {
        wallbox: { isWallboxCharging, status, isWallboxOnline },
    } = useTelemetries(TelemetryTypes.EMOBILITY);

    useEffect(() => {
        setIsCharging(
            isTheWallboxCharging({
                isWallboxCharging,
                status,
                isWallboxOnline,
            }),
        );
    }, [status, isWallboxOnline, isWallboxCharging]);

    return isCharging;
};

export const useWallboxConnectButton = (
    marketingTiles: MarketingTileModel[],
): boolean => {
    const tile = marketingTiles.find(
        (item) => item.type === MarketingTileTypes.WALLBOX,
    );

    return (
        !!tile &&
        tile.shouldRender &&
        checkForScopes([Scope.ENERGYDEVICES_EV_WRITE])
    );
};

export const useWallboxReadingStartDate = () => {
    const readingStartDate = useAppSelector(
        (state) =>
            state.emobility.wallboxData.wallbox?.deviceConfiguration
                ?.readingStartDate,
    );

    return useEmobilityReadingStartDate(readingStartDate);
};

export const useWallboxChargingModes = () => {
    const data = useAppSelector(
        (state) => state.emobility.wallboxData.chargingModes.data,
    );
    const permissions = useWallboxChargingModesPermissions();

    return data
        .filter((item) => item.mode !== GridFriendlyCharging)
        .filter((item) => permissions[item.mode]);
};

export const useWallboxSmartSchedulingData = () => {
    const schedulingData = useAppSelector((state) =>
        state.emobility.wallboxData.chargingModes.data.find(
            (item) => item.mode === Schedule,
        ),
    ) as ChargingModeData<ScheduleChargingModel> | undefined;

    const gridFriendlyData = useAppSelector((state) =>
        state.emobility.wallboxData.chargingModes.data.find(
            (item) => item.mode === GridFriendlyCharging,
        ),
    ) as ChargingModeData<ScheduleChargingModel> | undefined;

    /**
     * The smart schedule mode can use data from two
     * different sources: GridFriendly mode for UK or
     * the basic Schedule mode.
     * Currently, if we have an active GridFriendly mode,
     * then it will be used as source of data
     */
    const data = gridFriendlyData?.parameters.active
        ? gridFriendlyData
        : schedulingData;

    return useMemo(
        () => ({
            active: !!data?.parameters.active,
            hasData: !!data?.parameters,
            defaultEnd: data?.parameters.end ?? '1200',
            defaultStart: data?.parameters.start ?? '0000',
            isInitialized: data?.parameters.active !== undefined,
        }),
        [data?.parameters],
    );
};

export const useWallboxSolarAssistedChargingData = () => {
    const data = useAppSelector((state) =>
        state.emobility.wallboxData.chargingModes.data.find(
            (item) => item.mode === SolarAssistedCharging,
        ),
    ) as ChargingModeData<SolarAssistedChargingModel> | undefined;

    return useMemo(
        () => ({
            active: !!data?.parameters.active,
            pvPeakThreshold: (data?.parameters.pvPeakThreshold || 0.15) * 100,
            nightChargingEnd: data?.parameters?.nightCharging?.end || '2200',
            nightChargingStart:
                data?.parameters?.nightCharging?.start || '1700',
            nightChargingFallback: !!data?.parameters?.nightChargingFallback,
        }),
        [
            data?.parameters.active,
            data?.parameters.pvPeakThreshold,
            data?.parameters?.nightCharging?.end,
            data?.parameters?.nightCharging?.start,
            data?.parameters?.nightChargingFallback,
        ],
    );
};

export const useWallboxSafetyChargingData = () => {
    const data = useAppSelector((state) =>
        state.emobility.wallboxData.chargingModes.data.find(
            (item) => item.mode === SafetyCharging,
        ),
    ) as ChargingModeData<SafetyChargingModel> | undefined;

    const { active, minSoc, isInitialized } = useMemo(
        () => ({
            active: !!data?.parameters.active,
            minSoc: data?.parameters.minSoc ?? 20,
            isInitialized: data?.parameters.active !== undefined,
        }),
        [data?.parameters],
    );

    return useMemo(
        () => ({
            active,
            minSoc,
            isInitialized,
        }),
        [active, minSoc, isInitialized],
    );
};

export const useWallboxProgramChargingDefaultValues = () =>
    useMemo(
        () => ({
            defaultBattery: 80,
            defaultDepartureTime: '0700',
        }),
        [],
    );

export const useWallboxProgramChargingData = () => {
    const data = useAppSelector((state) =>
        state.emobility.wallboxData.chargingModes.data.find(
            (item) => item.mode === ProgramCharging,
        ),
    ) as ChargingModeData<ProgramChargingModel> | undefined;

    const { defaultBattery, defaultDepartureTime } =
        useWallboxProgramChargingDefaultValues();

    const { active, departureTimestamp, minSoc, userSoc } =
        data?.parameters ?? {};

    const isInitialized = useMemo(
        () => data?.parameters.active !== undefined,
        [data?.parameters.active],
    );

    return useMemo(
        () => ({
            active: !!active,
            departureTimestamp: departureTimestamp ?? defaultDepartureTime,
            minSoc: minSoc ?? defaultBattery,
            userSoc,
            isInitialized,
        }),
        [
            active,
            departureTimestamp,
            defaultDepartureTime,
            minSoc,
            defaultBattery,
            userSoc,
            isInitialized,
        ],
    );
};

export const useWallboxSmartChargingDefaultValues = () =>
    useMemo(
        () => ({
            defaultDepartureTime: '0700',
            defaultBatteryAtDeparture: 80,
            defaultMinBattery: 20,
        }),
        [],
    );

export const useWallboxSmartChargingData = () => {
    const data = useAppSelector((state) =>
        state.emobility.wallboxData.chargingModes.data.find(
            (item) => item.mode === GridxSmartCharging,
        ),
    ) as ChargingModeData<GridxSmartChargingResponseModel> | undefined;

    const {
        defaultDepartureTime,
        defaultBatteryAtDeparture,
        defaultMinBattery,
    } = useWallboxSmartChargingDefaultValues();

    const {
        active,
        departureTimestamp,
        minSoc,
        userSoc,
        departureSoc,
        isAwaitingSmartChargingUserSoc,
    } = data?.parameters ?? {};

    const isInitialized = useMemo(
        () => data?.parameters.active !== undefined,
        [data?.parameters.active],
    );

    return useMemo(
        () => ({
            active: !!active,
            departureTimestamp: departureTimestamp ?? defaultDepartureTime,
            userSoc,
            departureSoc: departureSoc ?? defaultBatteryAtDeparture,
            minSoc: minSoc ?? defaultMinBattery,
            isInitialized,
            isAwaitingSmartChargingUserSoc,
        }),
        // prettier-ignore
        [active, departureTimestamp, defaultDepartureTime, userSoc, departureSoc, defaultBatteryAtDeparture, minSoc, defaultMinBattery, isInitialized, isAwaitingSmartChargingUserSoc],
    );
};

export const useWallboxFastChargingData = () => {
    const data = useAppSelector((state) =>
        state.emobility.wallboxData.chargingModes.data.find(
            (item) => item.mode === FastCharging,
        ),
    ) as ChargingModeData<FastChargeModel> | undefined;

    return useMemo(
        () => ({
            active: !!data?.parameters.active,
        }),
        [data?.parameters.active],
    );
};

export const useWallboxBidirectionalChargingData = () => {
    const data = useAppSelector((state) =>
        state.emobility.wallboxData.chargingModes.data.find(
            (item) => item.mode === ProgramCharging,
        ),
    ) as ChargingModeData<ProgramChargingModel> | undefined;

    const DEFAULT_DEPARTURE_TIME = useMemo(() => '0700', []);
    const DEFAULT_MIN_SOC = useMemo(() => 80, []);
    // TODO: to be fetched from ENode if applicable, otherwise remain undefined
    // TODO: to be changed in EONFEH-18820
    // DEFAULT_USER_SOC is undefined by default in order to force users to enter the current battery level
    const DEFAULT_USER_SOC = useMemo(() => undefined, []);

    const { active, departureTimestamp, minSoc, userSoc } =
        data?.parameters ?? {};

    const isInitialized = useMemo(() => active !== undefined, [active]);

    return useMemo(
        () => ({
            active: !!active,
            departureTimestamp,
            minSoc,
            userSoc,
            DEFAULT_DEPARTURE_TIME,
            DEFAULT_MIN_SOC,
            DEFAULT_USER_SOC,
            isInitialized,
        }),
        [
            active,
            departureTimestamp,
            minSoc,
            userSoc,
            DEFAULT_DEPARTURE_TIME,
            DEFAULT_MIN_SOC,
            DEFAULT_USER_SOC,
            isInitialized,
        ],
    );
};

export const useWallboxPlugAndChargeData = () => {
    const data = useAppSelector((state) =>
        state.emobility.wallboxData.chargingModes.data.find(
            (item) => item.mode === HomePlugAndCharge,
        ),
    ) as ChargingModeData<PlugAndChargeModel> | undefined;

    return useMemo(
        () => ({
            active: !!data?.parameters.active,
            error: data?.error,
        }),
        [data?.error, data?.parameters.active],
    );
};

export const useWallboxDynamicPriceChargingData = () => {
    const data = useAppSelector((state) =>
        state.emobility.wallboxData.chargingModes.data.find(
            (item) => item.mode === DynamicPriceCharging,
        ),
    ) as ChargingModeData<DynamicPriceChargingModel> | undefined;

    return useMemo(
        () => ({
            active: !!data?.parameters.active,
            schedulingTimes: data?.parameters.schedulingTimes,
            isInitialized: data?.parameters.active !== undefined,
        }),
        [data?.parameters.active, data?.parameters.schedulingTimes],
    );
};

export const useWallboxLastChargingSessionData = () =>
    useAppSelector(
        (state) => state.insights?.emobility?.wallbox.lastChargingSession,
    );

type useWallboxChargingModesPermissionsReturnType = {
    [V in GenericSmartChargingModelModeEnum]?: boolean;
} & { bidirectionalCharging: boolean; gridxFastCharging: boolean };

export const useWallboxChargingModesPermissions =
    (): useWallboxChargingModesPermissionsReturnType => {
        const isGridXCustomer = useIsGridXCustomer();
        return {
            [Schedule]:
                checkForScopes([Scope.EMOBILITY_SMART_CHARGING_READ]) &&
                !isGridXCustomer,
            [SolarAssistedCharging]: checkForScopes([
                Scope.EMOBILITY_SOLAR_CHARGING_READ,
            ]),
            [ProgramCharging]:
                checkForScopes([Scope.EMOBILITY_SMART_CHARGING_READ]) &&
                !checkForScopes([Scope.EMOBILITY_COMBINED_CHARGING_READ]) &&
                !checkForScopes([
                    Scope.EMOBILITY_BIDIRECTIONAL_CHARGING_READ,
                ]) &&
                isGridXCustomer,
            [GridFriendlyCharging]: checkForScopes([
                Scope.EMOBILITY_SMART_CHARGING_READ,
            ]),
            [DynamicPriceCharging]: checkForScopes([
                Scope.EMOBILITY_DYNAMIC_PRICE_CHARGING_READ,
            ]),
            [HomePlugAndCharge]: checkForScopes([
                Scope.EMOBILITY_SMART_CHARGING_READ,
            ]),
            [SafetyCharging]:
                checkForScopes([Scope.EMOBILITY_SMART_CHARGING_READ]) &&
                !checkForScopes([Scope.EMOBILITY_COMBINED_CHARGING_READ]) &&
                isGridXCustomer,
            [GridxSmartCharging]:
                checkForScopes([Scope.EMOBILITY_SMART_CHARGING_READ]) &&
                checkForScopes([Scope.EMOBILITY_COMBINED_CHARGING_READ]) &&
                isGridXCustomer,
            gridxFastCharging:
                checkForScopes([Scope.EMOBILITY_SMART_CHARGING_READ]) &&
                isGridXCustomer,

            // Autopilot mode will be for electric car only for now
            // TODO: if wallbox shall get autopilot mode, add scope below
            [Autopilot]: false,
            bidirectionalCharging:
                checkForScopes([Scope.EMOBILITY_BIDIRECTIONAL_CHARGING_READ]) &&
                isGridXCustomer,
        };
    };

export const useWallboxChargingModesCount = () => {
    const permissions = useWallboxChargingModesPermissions();
    return Object.keys(permissions).reduce(
        (acc: boolean[], curr: GenericSmartChargingModelModeEnum) => {
            if (
                ![
                    GenericSmartChargingModelModeEnum.GridFriendlyCharging,
                    GenericSmartChargingModelModeEnum.HomePlugAndCharge,
                ].includes(curr) &&
                permissions[curr]
            ) {
                acc.push(true);
            }

            return acc;
        },
        [],
    ).length;
};

export const useEvSettings = () =>
    useAppSelector(
        (state) =>
            state.emobility?.wallboxData?.wallbox?.deviceConfiguration
                ?.vehicles?.[0],
    );
